import { Link, useParams } from "react-router-dom";
import { ArrowRight, BookOpen, CalendarDays, UserRound } from "lucide-react";
import PublicLayout from "@/components/public/PublicLayout";
import { Seo, breadcrumbJsonLd, faqJsonLd } from "@/components/public/Seo";
import { GREEN, BLUE, ORANGE } from "@/components/landing/shared";
import content from "./publicContent.json";
import guides from "./guides.json";
import { NotFoundPage } from "./index";

const fmtDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });

// Testo con link inline [etichetta](/percorso) → <a href> reali.
export function Inline({ text }) {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\))/g);
  return parts.map((p, i) => {
    const m = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    return m
      ? <Link key={i} to={m[2]} className="font-semibold underline underline-offset-2" style={{ color: BLUE }}>{m[1]}</Link>
      : <span key={i}>{p}</span>;
  });
}

function Block({ b }) {
  const h2 = "text-xl md:text-2xl font-extrabold tracking-tight text-slate-900 mt-12";
  const p = "mt-4 text-sm md:text-base text-slate-700 leading-relaxed";
  switch (b.type) {
    case "h2": return <h2 className={h2} style={{ fontFamily: "Manrope, sans-serif" }}>{b.text}</h2>;
    case "p": return <p className={p}><Inline text={b.text} /></p>;
    case "ul": return <ul className="mt-4 list-disc pl-6 space-y-2 text-sm md:text-base text-slate-700">{b.items.map((t) => <li key={t}><Inline text={t} /></li>)}</ul>;
    case "ol": return <ol className="mt-4 list-decimal pl-6 space-y-2 text-sm md:text-base text-slate-700">{b.items.map((t) => <li key={t}><Inline text={t} /></li>)}</ol>;
    case "formula": return <p className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 font-mono text-sm text-slate-800">{b.text}</p>;
    case "table": return (
      <div className="mt-4 overflow-x-auto"><table className="w-full text-sm border border-slate-200">
        <thead className="bg-slate-50"><tr>{b.head.map((h) => <th key={h} className="text-left font-bold px-3 py-2 border-b border-slate-200">{h}</th>)}</tr></thead>
        <tbody>{b.rows.map((r) => <tr key={r[0]} className="border-b border-slate-100">{r.map((c, i) => <td key={i} className="px-3 py-2 align-top text-slate-700">{c}</td>)}</tr>)}</tbody>
      </table></div>
    );
    default: return null;
  }
}

function DemoBox() {
  return (
    <aside className="mt-14 rounded-2xl p-8 text-center" data-testid="guide-demo-box"
      style={{ background: `linear-gradient(120deg, ${GREEN} 0%, #14564A 45%, ${BLUE} 100%)` }}>
      <h2 className="text-xl md:text-2xl font-extrabold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>Richiedi una demo</h2>
      <p className="mt-3 text-sm text-white/85 max-w-xl mx-auto">Vuoi vedere come ESG-PMI applica questa guida alla tua impresa? Una demo guidata sul tuo caso.</p>
      <Link to="/richiedi-demo" className="mt-6 inline-flex items-center gap-2 text-sm font-bold px-8 py-3.5 rounded-md"
        style={{ background: ORANGE, color: BLUE }} data-testid="guide-cta-demo">Richiedi una demo <ArrowRight className="w-4 h-4" aria-hidden="true" /></Link>
    </aside>
  );
}

export function GuideIndexPage() {
  const ix = guides.index;
  return (
    <PublicLayout breadcrumb={[{ name: "Guide", path: "/guide" }]}>
      <Seo title={ix.title} description={ix.description} path="/guide"
        jsonLd={[breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Guide", path: "/guide" }])]} />
      <article className="max-w-4xl mx-auto px-5 lg:px-8 pt-12 pb-20" data-testid="guide-index-page">
        <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: ORANGE }}>Guide</p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>{ix.h1}</h1>
        <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">{ix.intro}</p>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {guides.guides.map((g) => (
            <Link key={g.slug} to={g.path} className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-col hover:-translate-y-0.5 transition-transform" data-testid={`guide-card-${g.slug}`}>
              <BookOpen className="w-6 h-6" style={{ color: GREEN }} aria-hidden="true" />
              <h2 className="mt-4 text-lg font-extrabold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>{g.label}</h2>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{g.summary}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold" style={{ color: BLUE }}>Leggi la guida <ArrowRight className="w-4 h-4" aria-hidden="true" /></span>
            </Link>
          ))}
        </div>
      </article>
    </PublicLayout>
  );
}

export default function GuidePage() {
  const { slug } = useParams();
  const g = guides.guides.find((x) => x.slug === slug);
  if (!g) return <NotFoundPage />;
  const others = guides.guides.filter((x) => x.slug !== slug);
  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "Article", headline: g.h1, description: g.description,
      datePublished: g.datePublished, dateModified: g.dateModified, inLanguage: "it-IT",
      mainEntityOfPage: `${content.siteUrl}${g.path}`, image: `${content.siteUrl}/og-image.png`,
      author: { "@type": "Person", name: guides.authorName },
      publisher: { "@type": "Organization", name: "ESG-PMI", logo: { "@type": "ImageObject", url: `${content.siteUrl}/logo-esg-pmi.png` } },
    },
    breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Guide", path: "/guide" }, { name: g.label, path: g.path }]),
    faqJsonLd(g.faq),
  ];
  return (
    <PublicLayout breadcrumb={[{ name: "Guide", path: "/guide" }, { name: g.label, path: g.path }]}>
      <Seo title={g.title} description={g.description} path={g.path} jsonLd={jsonLd} />
      <article className="max-w-3xl mx-auto px-5 lg:px-8 pt-10 pb-20" data-testid={`guide-page-${g.slug}`}>
        <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: ORANGE }}>Guida</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>{g.h1}</h1>
        <p className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500" data-testid="guide-meta">
          <span className="inline-flex items-center gap-1.5"><UserRound className="w-3.5 h-3.5" aria-hidden="true" /> {guides.author}</span>
          <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" aria-hidden="true" /> Pubblicato il <time dateTime={g.datePublished}>{fmtDate(g.datePublished)}</time></span>
          <span>Aggiornato il <time dateTime={g.dateModified}>{fmtDate(g.dateModified)}</time></span>
        </p>
        {g.blocks.map((b, i) => <Block key={i} b={b} />)}
        <section className="mt-12" data-testid="guide-faq">
          <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Domande frequenti</h2>
          <div className="mt-6 space-y-3">
            {g.faq.map((f) => (
              <details key={f.q} className="rounded-xl border border-slate-200 bg-white group">
                <summary className="cursor-pointer select-none px-5 py-4 text-sm font-bold text-slate-900 list-none flex items-center justify-between gap-3">
                  {f.q}<span className="text-slate-400 group-open:rotate-90 transition-transform" aria-hidden="true">›</span>
                </summary>
                <p className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
        {g.sources && (
          <section className="mt-12">
            <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Fonti</h2>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-sm text-slate-600">{g.sources.map((s) => <li key={s}>{s}</li>)}</ul>
          </section>
        )}
        <DemoBox />
        <nav aria-label="Approfondimenti" className="mt-12 border-t border-slate-200 pt-8" data-testid="guide-related">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Continua a leggere</p>
          <ul className="space-y-2 text-sm">
            <li><Link to={g.product.path} className="font-semibold underline underline-offset-2" style={{ color: BLUE }} data-testid="guide-product-link">{g.product.label}</Link></li>
            {others.map((o) => <li key={o.slug}><Link to={o.path} className="font-semibold underline underline-offset-2" style={{ color: BLUE }}>Guida: {o.h1}</Link></li>)}
            <li><Link to="/guide" className="font-semibold underline underline-offset-2" style={{ color: BLUE }}>Tutte le guide</Link></li>
          </ul>
        </nav>
      </article>
    </PublicLayout>
  );
}
